// Build 96.2 — Admin Platser-sammelvy.
// Visar alla 4 collections (karttipps, foretag, forening, vardagskort) i en
// gemensam läsvy. Edit-knappen växlar till rätt tab + öppnar originalformuläret.
// Inga datamodellsändringar; ingen direkt mutation.

import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Pencil, MapPin, AlertTriangle, ExternalLink, Trash2,
  // Build 96.15 — moderations-livscykel
  CheckCircle2, EyeOff, PauseCircle, Archive, RotateCw,
  // Build 96.3 — hällristningsinspirerad ikonstil, stroke 1.5
  Users, TreePine, Home, Coffee, LifeBuoy, Landmark, ShoppingBasket, List, BookOpen,
} from "lucide-react";
import {
  FILTER_GROUPS,
  KARTMENY_LABEL,
  getFallbackKartmeny,
} from "@/utils/kartmeny";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const modHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem("mod_token")}` });

const SOURCE_TO_TAB = {
  karttipps: "karttipps",
  foretag: "tjanstekort",
  forening: "foreningar",
  vardagskort: "vardagskort",
};

// Build 96.15 — Moderationsläge (livscykel för Platser).
// aktiv: standard, publik.  granskas: internt bara, saknar info.
// deaktiverad: internt bara, ej relevant just nu.  arkiverad: internt bara, endast admin.
const MODERATION_META = {
  aktiv: {
    label: "Aktiv",
    color: "bg-pine/10 text-pine border-pine/30",
    icon: CheckCircle2,
    hint: "Syns publikt (karta, sök, listor).",
  },
  granskas: {
    label: "I granskning",
    color: "bg-amber-100 text-amber-800 border-amber-300",
    icon: EyeOff,
    hint: "Endast moderatorer — publikt dolt medan uppgifter kontrolleras.",
  },
  deaktiverad: {
    label: "Deaktiverad",
    color: "bg-slate-200 text-slate-700 border-slate-300",
    icon: PauseCircle,
    hint: "Publikt dolt men fullständigt bevarat. Kan aktiveras igen när som helst.",
  },
  arkiverad: {
    label: "Arkiverad",
    color: "bg-neutral-800/10 text-neutral-700 border-neutral-400",
    icon: Archive,
    hint: "Historisk. Endast administratör får aktivera igen.",
  },
};

const MODERATION_ORDER = ["aktiv", "granskas", "deaktiverad", "arkiverad"];

// Build 96.3 — hällristningsinspirerad ikonstil: enkla, ruhiga Lucide-ikoner
// med tunn streckföring. Single source of truth för admin-sammelvyn.
const KARTMENY_ICONS = {
  folk_foreningar: Users,
  friluft_idrott: TreePine,
  hemma_byarna: Home,
  loppis_fika: Coffee,
  praktisk_hjalp: LifeBuoy,
  utflykter_historia: Landmark,
  vardag: ShoppingBasket,
  visa_allt: List,
};
// Litet sekundärtecken för "Bygdens berättelser" — visas bredvid hemma_byarna-ikonen.
const HEMMA_STORY_ICON = BookOpen;

// För publik karta: ord+typ-prefix av nummersökning, t.ex. "karttipp", "förening".
const SOURCE_NUMBER_PARSERS = [
  { test: /^karttipp(?:s)?\s*#?\s*(\d+)\s*$/i, source: "karttipps" },
  { test: /^k\s*#?\s*(\d+)\s*$/i, source: "karttipps" },
  { test: /^f(?:öretag|oretag)\s*#?\s*(\d+)\s*$/i, source: "foretag" },
  { test: /^företag\s*#?\s*(\d+)\s*$/i, source: "foretag" },
  { test: /^f(?:örening|orening)(?:ar)?\s*#?\s*(\d+)\s*$/i, source: "forening" },
  { test: /^förening(?:ar)?\s*#?\s*(\d+)\s*$/i, source: "forening" },
  { test: /^vardagskort\s*#?\s*(\d+)\s*$/i, source: "vardagskort" },
  { test: /^v\s*#?\s*(\d+)\s*$/i, source: "vardagskort" },
];

// Tolka söksträng: returnera {nr, source?} om strängen ser ut som nummersökning.
function parseNumberSearch(raw) {
  const s = String(raw || "").trim();
  if (!s) return null;
  // Ren nummer: "33" eller "#33" → matcha ALLA collections med nr=33
  const pureNr = s.match(/^#?\s*(\d+)\s*$/);
  if (pureNr) return { nr: parseInt(pureNr[1], 10), source: null };
  // Typ + nummer
  for (const p of SOURCE_NUMBER_PARSERS) {
    const m = s.match(p.test);
    if (m) return { nr: parseInt(m[1], 10), source: p.source };
  }
  return null;
}

const SOURCES = [
  { key: "all", label: "Alla källor" },
  { key: "karttipps", label: "Karttipps" },
  { key: "foretag", label: "Företag" },
  { key: "forening", label: "Föreningar" },
  { key: "vardagskort", label: "Vardagskort" },
];

const COORD_FILTERS = [
  { key: "all", label: "Alla" },
  { key: "with", label: "Med koordinater" },
  { key: "without", label: "Utan koordinater" },
];

const KARTMENY_FILTERS = [
  { key: "all", label: "Alla kartmeny-värden" },
  { key: "__none__", label: "Utan kartmeny" },
  ...FILTER_GROUPS.filter((g) => !g.all).map((g) => ({ key: g.key, label: g.label })),
];

// Plausibilitetscheck för vidalv.live (Norrlands inland, ungefärlig box).
function coordsLookSuspect(row) {
  if (!row.has_coords) return false;
  const { lat, lng } = row;
  if (typeof lat !== "number" || typeof lng !== "number") return false;
  return !(lat >= 62 && lat <= 66 && lng >= 13 && lng <= 19);
}

export default function PlatserOversiktPanel({ onOpenEdit, onCreateKarttipp, currentUser }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState("");
  const [srcFilter, setSrcFilter] = useState("all");
  const [coordFilter, setCoordFilter] = useState("all");
  const [kartmenyFilter, setKartmenyFilter] = useState("all");
  // Build 96.3 — sortering: "nr" (per typ + nummer) eller "az" (alfabetiskt över alla)
  const [sortMode, setSortMode] = useState("nr");
  // Build 96.4 — vilken rad sparas just nu (för disabled-state på dropdown)
  const [savingKey, setSavingKey] = useState(null);
  // Build 96.15 — filter för moderationsläge (aktiv/granskas/deaktiverad/arkiverad/alla).
  // Grundinställning: "aktiv+granskas+deaktiverad" — arkiverade döljs som default
  // så listan känns fokuserad. Väljer man "arkiverad" eller "alla" ser man arkiverat.
  const [moderationFilter, setModerationFilter] = useState("standard");

  const isAdmin = !!currentUser && (currentUser.role === "admin" || currentUser.role === "master");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    axios
      .get(`${API}/mod/platser/overview`, { headers: modHeaders() })
      .then((r) => { if (!cancelled) setRows(r.data?.rows || []); })
      .catch(() => { if (!cancelled) setErr("Kunde inte hämta sammelvyn."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Build 96.4 — Inline kartmeny-tilldelning: PATCH + lokal mutering för snabb feedback.
  const updateKartmenyInline = async (row, newValue) => {
    const key = `${row.source}-${row.id}`;
    setSavingKey(key);
    try {
      // Tolka "__clear__" och "visa_allt" som rensa-kommando i UI; backend hanterar bägge.
      const payload = (newValue === "__clear__" || newValue === "visa_allt" || newValue === "")
        ? { kartmeny: null }
        : { kartmeny: newValue };
      await axios.patch(
        `${API}/mod/platser/${row.source}/${row.id}/kartmeny`,
        payload,
        { headers: modHeaders() }
      );
      // Mutera raden lokalt så listan uppdateras direkt utan extra fetch
      setRows((prev) => prev.map((r) => (r.source === row.source && r.id === row.id)
        ? { ...r, kartmeny: payload.kartmeny || null }
        : r
      ));
      if (payload.kartmeny) {
        toast.success(`${row.nr_label}: Kartmeny satt till "${KARTMENY_LABEL[payload.kartmeny]}"`);
      } else {
        toast.success(`${row.nr_label}: Kartmeny rensad (visas via fallback)`);
      }
    } catch (e) {
      toast.error(`Kunde inte spara kartmeny: ${e?.response?.data?.detail || e.message || "okänt fel"}`);
    } finally {
      setSavingKey(null);
    }
  };

  // Build 96.5 — Soft-Delete från sammelvyn. Använder den generiska
  // /api/mod/platser/{source}/{id}-routen (status='raderad' + audit-fält).
  // Bekräftelsedialog visar typ + nummer + namn. Lokal state-update efter lyckad delete
  // så raden försvinner direkt utan full refetch.
  const softDeleteRow = async (row) => {
    const name = row.namn || "(utan namn)";
    const msg =
      `Vill du ta bort ${row.source_label} #${row.nr || "?"} – ${name}?\n\n` +
      `Posten döljs från publika vyer men kan återställas av admin.`;
    if (!window.confirm(msg)) return;
    const key = `${row.source}-${row.id}`;
    setSavingKey(key);
    try {
      await axios.delete(
        `${API}/mod/platser/${row.source}/${row.id}`,
        { headers: modHeaders() }
      );
      setRows((prev) => prev.filter((r) => !(r.source === row.source && r.id === row.id)));
      toast.success("Borttaget", {
        description: `${row.source_label} #${row.nr || "?"} – döljs nu från publika vyer`,
        duration: 3000,
      });
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "okänt fel";
      toast.error(`Kunde inte ta bort: ${detail}`);
    } finally {
      setSavingKey(null);
    }
  };

  // Build 96.15 — Sätt moderation_status via ny PATCH-route. Skriver ENDAST moderations-
  // fälten på backend (name/beskrivning/koordinater/kategorier/tags/historik rörs aldrig).
  // Ber om valfri anledning via prompt(). "arkiverad" kräver admin (backend enforcerar också).
  const setModerationStatus = async (row, newStatus) => {
    const meta = MODERATION_META[newStatus];
    const nrText = `${row.source_label} #${row.nr || "?"} – ${row.namn || "(utan namn)"}`;
    if (newStatus === "arkiverad" && !isAdmin) {
      toast.error("Endast administratör får arkivera.");
      return;
    }
    // Bekräftelse + valfri anledning.
    const askReason =
      `Ändra ${nrText} till "${meta.label}"?\n\n` +
      `${meta.hint}\n\n` +
      `Ange (valfritt) kort anledning — t.ex. saisong slut, betreiber okänd, dublett:`;
    const reason = window.prompt(askReason, "");
    if (reason === null) return; // Avbrutet
    const key = `${row.source}-${row.id}`;
    setSavingKey(key);
    try {
      const r = await axios.patch(
        `${API}/mod/platser/${row.source}/${row.id}/moderation-status`,
        { status: newStatus, reason: reason.trim() || null },
        { headers: modHeaders() }
      );
      const savedMs = r.data?.moderation_status || newStatus;
      const savedReason = r.data?.moderation_reason || (reason.trim() || null);
      setRows((prev) => prev.map((x) => (x.source === row.source && x.id === row.id)
        ? { ...x, moderation_status: savedMs, moderation_reason: savedReason,
            moderation_updated_at: new Date().toISOString(),
            moderation_updated_by: (currentUser?.email || currentUser?.name || "master") }
        : x
      ));
      toast.success(`${nrText}: ${meta.label}`, {
        description: reason.trim() ? `Anledning: ${reason.trim()}` : "Ingen anledning angiven",
        duration: 3200,
      });
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "okänt fel";
      toast.error(`Kunde inte ändra status: ${detail}`);
    } finally {
      setSavingKey(null);
    }
  };

  // Anrika varje rad med fallback_kartmeny (beräknat klient-side via samma logik som publika kartan).
  const enriched = useMemo(
    () => rows.map((r) => ({ ...r, fallback_kartmeny: r.kartmeny ? null : getFallbackKartmeny(r) })),
    [rows]
  );

  const filtered = useMemo(() => {
    const q = search.trim();
    const numMatch = parseNumberSearch(q);
    const qLower = q.toLowerCase();
    const list = enriched.filter((r) => {
      if (srcFilter !== "all" && r.source !== srcFilter) return false;
      if (coordFilter === "with" && !r.has_coords) return false;
      if (coordFilter === "without" && r.has_coords) return false;
      if (kartmenyFilter === "__none__" && r.kartmeny) return false;
      if (kartmenyFilter !== "all" && kartmenyFilter !== "__none__" && r.kartmeny !== kartmenyFilter) return false;
      // Build 96.15 — moderation-filter
      const ms = r.moderation_status || "aktiv";
      if (moderationFilter === "aktiv" && ms !== "aktiv") return false;
      if (moderationFilter === "granskas" && ms !== "granskas") return false;
      if (moderationFilter === "deaktiverad" && ms !== "deaktiverad") return false;
      if (moderationFilter === "arkiverad" && ms !== "arkiverad") return false;
      // "standard" = alla utom arkiverad; "alla" = allt.
      if (moderationFilter === "standard" && ms === "arkiverad") return false;
      if (!q) return true;
      // Nummersökning har företräde: matchar exakt nummer (+ ev. typ)
      if (numMatch) {
        if (r.nr !== numMatch.nr) return false;
        if (numMatch.source && r.source !== numMatch.source) return false;
        return true;
      }
      // Fritextsök på namn/nr_label/ort/adress
      const hay = `${r.namn || ""} ${r.nr_label || ""} ${r.ort || ""} ${r.adress || ""}`.toLowerCase();
      return hay.includes(qLower);
    });
    // Build 96.3 — sortering
    if (sortMode === "az") {
      const coll = new Intl.Collator("sv", { sensitivity: "base", numeric: true });
      list.sort((a, b) => coll.compare(a.namn || "", b.namn || ""));
    } else {
      // "nr": stabilt per (source, nr)
      const SRC_ORDER = { karttipps: 0, foretag: 1, forening: 2, vardagskort: 3 };
      list.sort((a, b) => {
        const sa = SRC_ORDER[a.source] ?? 99;
        const sb = SRC_ORDER[b.source] ?? 99;
        if (sa !== sb) return sa - sb;
        const na = a.nr ?? 999999;
        const nb = b.nr ?? 999999;
        return na - nb;
      });
    }
    return list;
  }, [enriched, search, srcFilter, coordFilter, kartmenyFilter, sortMode, moderationFilter]);

  const stats = useMemo(() => {
    const s = { total: enriched.length, per_source: {}, no_coords: 0, no_kartmeny: 0, suspect: 0, per_ms: { aktiv: 0, granskas: 0, deaktiverad: 0, arkiverad: 0 } };
    for (const r of enriched) {
      s.per_source[r.source] = (s.per_source[r.source] || 0) + 1;
      if (!r.has_coords) s.no_coords += 1;
      if (!r.kartmeny) s.no_kartmeny += 1;
      if (coordsLookSuspect(r)) s.suspect += 1;
      const ms = r.moderation_status || "aktiv";
      if (s.per_ms[ms] !== undefined) s.per_ms[ms] += 1;
    }
    return s;
  }, [enriched]);

  return (
    <div data-testid="platser-oversikt" className="mb-8 bg-white border border-warm-border rounded-lg shadow-sm">
      <div className="px-4 py-3 border-b border-warm-border flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-heading text-base font-semibold text-ink">Platser — sammelvy</h3>
          <p className="text-xs text-bark/70 mt-0.5">
            Alla kartobjekt från fyra källor (karttipps, företag, föreningar, vardagskort). Klicka på <Pencil size={11} className="inline -mt-0.5" /> för att öppna originalformuläret.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {loading ? <span className="text-xs text-bark/60">Laddar…</span> : null}
          {onCreateKarttipp && (
            <button
              type="button"
              onClick={onCreateKarttipp}
              data-testid="sammelvy-create-karttipp"
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-falu hover:bg-falu-hover text-white px-3 py-1.5 rounded transition-colors"
            >
              + Skapa ny karttipp
            </button>
          )}
        </div>
      </div>

      {/* Build 96.4 — Klickbare statistik som arbetsmenü.
          Varje chip togglar aktuell filterkombo så moderatorn snabbt kan
          fokusera på "alla", per källa, eller utan koordinater/kartmeny. */}
      <div className="px-4 py-2.5 flex flex-wrap gap-1.5 text-xs border-b border-warm-border bg-bone-alt/30">
        {(() => {
          const isAllReset = srcFilter === "all" && coordFilter === "all" && kartmenyFilter === "all";
          const baseCls = "inline-flex items-center gap-1 px-2.5 py-1 rounded-full border transition-colors";
          const activeCls = "bg-ink text-parchment border-ink shadow-sm";
          const inactive = (color) => `bg-parchment border-warm-border hover:border-falu/60 ${color || "text-bark"}`;
          return (
            <>
              <button type="button" data-testid="stat-total" aria-pressed={isAllReset}
                onClick={() => { setSrcFilter("all"); setCoordFilter("all"); setKartmenyFilter("all"); setSearch(""); }}
                className={`${baseCls} ${isAllReset ? activeCls : inactive()}`}>
                <strong>{stats.total}</strong><span>totalt</span>
              </button>
              <button type="button" data-testid="stat-karttipps" aria-pressed={srcFilter === "karttipps"}
                onClick={() => setSrcFilter(srcFilter === "karttipps" ? "all" : "karttipps")}
                className={`${baseCls} ${srcFilter === "karttipps" ? activeCls : inactive()}`}>
                <strong>{stats.per_source.karttipps || 0}</strong><span>karttipps</span>
              </button>
              <button type="button" data-testid="stat-foretag" aria-pressed={srcFilter === "foretag"}
                onClick={() => setSrcFilter(srcFilter === "foretag" ? "all" : "foretag")}
                className={`${baseCls} ${srcFilter === "foretag" ? activeCls : inactive()}`}>
                <strong>{stats.per_source.foretag || 0}</strong><span>företag</span>
              </button>
              <button type="button" data-testid="stat-forening" aria-pressed={srcFilter === "forening"}
                onClick={() => setSrcFilter(srcFilter === "forening" ? "all" : "forening")}
                className={`${baseCls} ${srcFilter === "forening" ? activeCls : inactive()}`}>
                <strong>{stats.per_source.forening || 0}</strong><span>föreningar</span>
              </button>
              <button type="button" data-testid="stat-vardagskort" aria-pressed={srcFilter === "vardagskort"}
                onClick={() => setSrcFilter(srcFilter === "vardagskort" ? "all" : "vardagskort")}
                className={`${baseCls} ${srcFilter === "vardagskort" ? activeCls : inactive()}`}>
                <strong>{stats.per_source.vardagskort || 0}</strong><span>vardagskort</span>
              </button>
              <button type="button" data-testid="stat-no-coords" aria-pressed={coordFilter === "without"}
                onClick={() => setCoordFilter(coordFilter === "without" ? "all" : "without")}
                className={`${baseCls} ${coordFilter === "without" ? activeCls : inactive("text-falu")}`}>
                <strong>{stats.no_coords}</strong><span>utan koordinater</span>
              </button>
              <button type="button" data-testid="stat-no-kartmeny" aria-pressed={kartmenyFilter === "__none__"}
                onClick={() => setKartmenyFilter(kartmenyFilter === "__none__" ? "all" : "__none__")}
                className={`${baseCls} ${kartmenyFilter === "__none__" ? activeCls : inactive("text-falu")}`}>
                <strong>{stats.no_kartmeny}</strong><span>utan kartmeny</span>
              </button>
              {stats.suspect > 0 && (
                <span data-testid="stat-suspect" className="inline-flex items-center gap-1 px-2.5 py-1 text-falu">
                  <AlertTriangle size={11} className="-mt-0.5" />
                  <strong>{stats.suspect}</strong> koordinater utanför inland-boxen
                </span>
              )}
            </>
          );
        })()}
      </div>

      {/* Build 96.15 — Moderationslivscykel-filter (aktiv/granskas/deaktiverad/arkiverad). */}
      <div className="px-4 py-2.5 flex flex-wrap gap-1.5 text-xs border-b border-warm-border bg-parchment/60">
        <span className="text-bark/60 self-center mr-1">Moderationsläge:</span>
        {(() => {
          const chips = [
            { key: "standard", label: "Aktiv + Granskas + Deakt.", count: stats.per_ms.aktiv + stats.per_ms.granskas + stats.per_ms.deaktiverad, testid: "ms-filter-standard" },
            { key: "aktiv",       label: "Aktiv",        count: stats.per_ms.aktiv,        testid: "ms-filter-aktiv" },
            { key: "granskas",    label: "I granskning", count: stats.per_ms.granskas,     testid: "ms-filter-granskas" },
            { key: "deaktiverad", label: "Deaktiverad",  count: stats.per_ms.deaktiverad,  testid: "ms-filter-deaktiverad" },
            { key: "arkiverad",   label: "Arkiverad",    count: stats.per_ms.arkiverad,    testid: "ms-filter-arkiverad" },
            { key: "alla",        label: "Alla (inkl. arkiv)", count: stats.total,          testid: "ms-filter-alla" },
          ];
          return chips.map((c) => {
            const active = moderationFilter === c.key;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => setModerationFilter(c.key)}
                data-testid={c.testid}
                aria-pressed={active}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border transition-colors ${active ? "bg-ink text-parchment border-ink shadow-sm" : "bg-parchment border-warm-border hover:border-falu/60 text-bark"}`}
              >
                <strong>{c.count}</strong><span>{c.label}</span>
              </button>
            );
          });
        })()}
      </div>

      {/* Filter-rad */}
      <div className="px-4 py-3 flex flex-wrap gap-2 items-center border-b border-warm-border">
        <input
          type="text"
          data-testid="platser-search"
          placeholder='Sök: namn · "#33" · "karttipp 33" · ort · adress'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[220px] px-3 py-1.5 border border-warm-border rounded-sm text-sm bg-white"
        />
        <select data-testid="platser-sort" value={sortMode} onChange={(e) => setSortMode(e.target.value)}
          className="px-2.5 py-1.5 border border-warm-border rounded-sm text-sm bg-white" title="Sortering">
          <option value="nr">Sortera efter nummer</option>
          <option value="az">Sortera A–Ö</option>
        </select>
        <select data-testid="platser-src-filter" value={srcFilter} onChange={(e) => setSrcFilter(e.target.value)}
          className="px-2.5 py-1.5 border border-warm-border rounded-sm text-sm bg-white">
          {SOURCES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <select data-testid="platser-coord-filter" value={coordFilter} onChange={(e) => setCoordFilter(e.target.value)}
          className="px-2.5 py-1.5 border border-warm-border rounded-sm text-sm bg-white">
          {COORD_FILTERS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <select data-testid="platser-kartmeny-filter" value={kartmenyFilter} onChange={(e) => setKartmenyFilter(e.target.value)}
          className="px-2.5 py-1.5 border border-warm-border rounded-sm text-sm bg-white">
          {KARTMENY_FILTERS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
        </select>
        <span className="text-xs text-bark/60" data-testid="platser-filter-count">
          {filtered.length} träffar
        </span>
      </div>

      {/* Lista */}
      {err && <p className="px-4 py-3 text-sm text-falu">{err}</p>}
      {!err && filtered.length === 0 && !loading && (
        <p className="px-4 py-6 text-sm text-bark/60 text-center">Inga träffar för aktuella filter.</p>
      )}
      <ul className="divide-y divide-warm-border max-h-[480px] overflow-y-auto" data-testid="platser-list">
        {filtered.map((r) => {
          const suspect = coordsLookSuspect(r);
          const fb = r.fallback_kartmeny;
          return (
            <li key={`${r.source}-${r.id}`} data-testid={`platser-row-${r.source}-${r.id}`}
              className="px-4 py-2.5 flex items-start gap-3 hover:bg-bone-alt/40">
              {/* Nr-badge */}
              <span className="shrink-0 inline-flex items-center text-[11px] font-mono font-semibold text-bark bg-parchment border border-warm-border rounded px-1.5 py-0.5 mt-0.5">
                {r.nr_label}
              </span>
              {/* Hauptbereich */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-ink leading-tight truncate">{r.namn || <span className="italic text-bark/60">(utan namn)</span>}</p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-bark/70 mt-0.5">
                  {r.status && <span className="px-1.5 py-0.5 bg-bone-alt rounded">{r.status}</span>}
                  {r.ort && <span>{r.ort}</span>}
                  {r.kategori && <span className="italic">{r.kategori}</span>}
                  {r.omrade && <span>område: {r.omrade}</span>}
                </div>
                {/* Koordinatenstatus */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] mt-0.5">
                  {r.has_coords ? (
                    <span data-testid={`coord-ok-${r.id}`} className={suspect ? "text-falu" : "text-pine"}>
                      <MapPin size={10} className="inline -mt-0.5" />{" "}
                      {r.lat?.toFixed(5)}, {r.lng?.toFixed(5)}
                      {suspect && <span className="ml-1 italic">(osäker — utanför inland-boxen)</span>}
                      {" · "}
                      <a
                        href={`https://www.google.com/maps?q=${r.lat},${r.lng}`}
                        target="_blank" rel="noopener noreferrer"
                        className="underline decoration-dotted hover:text-falu"
                        data-testid={`visa-pa-karta-${r.id}`}
                      >Visa på karta <ExternalLink size={9} className="inline -mt-0.5" /></a>
                    </span>
                  ) : (
                    <span data-testid={`coord-missing-${r.id}`} className="text-falu">
                      <AlertTriangle size={10} className="inline -mt-0.5" /> Saknar koordinater
                    </span>
                  )}
                </div>
                {/* Kartmeny-status (med ikon från single source of truth) */}
                <div className="text-[11px] mt-0.5 flex items-center gap-1.5 flex-wrap">
                  {r.kartmeny ? (() => {
                    const KIcon = KARTMENY_ICONS[r.kartmeny];
                    return (
                      <span data-testid={`kartmeny-set-${r.id}`} className="text-pine inline-flex items-center gap-1">
                        {KIcon && <KIcon size={11} strokeWidth={1.5} aria-hidden="true" />}
                        <span>Kartmeny: <strong>{KARTMENY_LABEL[r.kartmeny] || r.kartmeny}</strong></span>
                        {r.kartmeny === "hemma_byarna" && (
                          <HEMMA_STORY_ICON size={10} strokeWidth={1.5} className="text-bark/45" aria-label="Bygdens berättelser" />
                        )}
                      </span>
                    );
                  })() : (() => {
                    const FIcon = fb ? KARTMENY_ICONS[fb] : null;
                    return (
                      <span data-testid={`kartmeny-fallback-${r.id}`} className="text-bark/70 inline-flex items-center gap-1">
                        <span>Kartmeny: <em>ej satt</em></span>
                        {fb ? (
                          <span className="inline-flex items-center gap-1 italic">
                            · Visas via fallback:
                            {FIcon && <FIcon size={10} strokeWidth={1.5} aria-hidden="true" />}
                            <strong className="not-italic">{KARTMENY_LABEL[fb]}</strong>
                          </span>
                        ) : (
                          <span className="italic text-falu">· ingen fallback-grupp matchar</span>
                        )}
                      </span>
                    );
                  })()}
                </div>
                {/* Tags (max 5) */}
                {Array.isArray(r.taggar) && r.taggar.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {r.taggar.slice(0, 5).map((t, i) => (
                      <span key={i} className="text-[10px] text-bark/60 bg-bone-alt border border-warm-border/40 px-1.5 py-0.5 rounded">#{t}</span>
                    ))}
                  </div>
                )}
                {/* Build 96.4 — Inline kartmeny-tilldelning per post.
                    8 val: 7 lagrade keys + "Visa allt / rensa". */}
                <div className="mt-1.5 flex items-center gap-1.5 text-[11px]">
                  <label htmlFor={`set-kartmeny-${r.source}-${r.id}`} className="text-bark/65">Tilldela:</label>
                  <select
                    id={`set-kartmeny-${r.source}-${r.id}`}
                    data-testid={`set-kartmeny-${r.source}-${r.id}`}
                    value={r.kartmeny || ""}
                    disabled={savingKey === `${r.source}-${r.id}`}
                    onChange={(e) => updateKartmenyInline(r, e.target.value)}
                    className="text-[11px] border border-warm-border rounded px-1.5 py-0.5 bg-white disabled:opacity-60"
                  >
                    <option value="" disabled hidden>— Välj kartmeny —</option>
                    <option value="folk_foreningar">Folk & föreningar</option>
                    <option value="friluft_idrott">Friluft & idrott</option>
                    <option value="hemma_byarna">Hemma i byarna</option>
                    <option value="loppis_fika">Loppis & fika</option>
                    <option value="praktisk_hjalp">Praktisk hjälp</option>
                    <option value="utflykter_historia">Utflykter & historia</option>
                    <option value="vardag">Vardag</option>
                    <option value="__clear__">Visa allt / rensa kartmeny</option>
                  </select>
                  {savingKey === `${r.source}-${r.id}` && <span className="text-bark/60 italic">sparar…</span>}
                </div>
              </div>
              {/* Edit + Soft-Delete + Moderations-actions */}
              <div className="shrink-0 flex flex-col items-end gap-1">
                {/* Build 96.15 — status-badge + livscykel-aktioner */}
                {(() => {
                  const ms = r.moderation_status || "aktiv";
                  const meta = MODERATION_META[ms] || MODERATION_META.aktiv;
                  const Icon = meta.icon;
                  return (
                    <span
                      data-testid={`ms-badge-${r.source}-${r.id}`}
                      title={meta.hint + (r.moderation_reason ? ` · Anledning: ${r.moderation_reason}` : "")}
                      className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${meta.color}`}
                    >
                      <Icon size={11} />{meta.label}
                    </span>
                  );
                })()}
                <div className="flex items-center gap-0.5">
                  {/* Endast åtgärder som skiljer sig från aktuellt status visas. */}
                  {(() => {
                    const ms = r.moderation_status || "aktiv";
                    const key = `${r.source}-${r.id}`;
                    const btnBase = "p-1.5 rounded transition-colors disabled:opacity-40";
                    const actions = [];
                    if (ms !== "aktiv") {
                      actions.push(
                        <button
                          key="a"
                          type="button"
                          onClick={() => setModerationStatus(r, "aktiv")}
                          disabled={savingKey === key || (ms === "arkiverad" && !isAdmin)}
                          data-testid={`ms-set-aktiv-${r.source}-${r.id}`}
                          title={ms === "arkiverad" && !isAdmin ? "Endast admin kan reaktivera arkiverade" : "Aktivera – syns publikt igen"}
                          className={`${btnBase} text-pine hover:bg-pine/10`}
                        >
                          <RotateCw size={14} />
                        </button>
                      );
                    }
                    if (ms !== "granskas") {
                      actions.push(
                        <button
                          key="g"
                          type="button"
                          onClick={() => setModerationStatus(r, "granskas")}
                          disabled={savingKey === key || (ms === "arkiverad" && !isAdmin)}
                          data-testid={`ms-set-granskas-${r.source}-${r.id}`}
                          title="Sätt i granskning – döljs publikt tills kontrollerad"
                          className={`${btnBase} text-amber-700 hover:bg-amber-100`}
                        >
                          <EyeOff size={14} />
                        </button>
                      );
                    }
                    if (ms !== "deaktiverad") {
                      actions.push(
                        <button
                          key="d"
                          type="button"
                          onClick={() => setModerationStatus(r, "deaktiverad")}
                          disabled={savingKey === key || (ms === "arkiverad" && !isAdmin)}
                          data-testid={`ms-set-deaktiverad-${r.source}-${r.id}`}
                          title="Deaktivera – döljs publikt, all data bevaras"
                          className={`${btnBase} text-slate-600 hover:bg-slate-200`}
                        >
                          <PauseCircle size={14} />
                        </button>
                      );
                    }
                    if (ms !== "arkiverad" && isAdmin) {
                      actions.push(
                        <button
                          key="ark"
                          type="button"
                          onClick={() => setModerationStatus(r, "arkiverad")}
                          disabled={savingKey === key}
                          data-testid={`ms-set-arkiverad-${r.source}-${r.id}`}
                          title="Arkivera (endast admin) – historisk post"
                          className={`${btnBase} text-neutral-700 hover:bg-neutral-200`}
                        >
                          <Archive size={14} />
                        </button>
                      );
                    }
                    return actions;
                  })()}
                </div>
                <div className="flex items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => onOpenEdit({ source: r.source, tab: SOURCE_TO_TAB[r.source], id: r.id })}
                    data-testid={`platser-edit-${r.source}-${r.id}`}
                    title={`Redigera i ${r.source_label}-formuläret`}
                    className="p-1.5 text-bark hover:text-ink rounded hover:bg-bone-alt transition-colors"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => softDeleteRow(r)}
                    disabled={savingKey === `${r.source}-${r.id}`}
                    data-testid={`platser-delete-${r.source}-${r.id}`}
                    title="Ta bort — döljer från publika vyer (kan återställas av admin)"
                    className="p-1.5 text-bark hover:text-falu rounded hover:bg-falu/10 transition-colors disabled:opacity-50"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
